import { z } from 'zod';
import { QUOTE_VALIDITY_DAYS_MAX } from '../constants/company.js';
import { idSchema, isoDateSchema, isoDateTimeSchema, moneySchema } from './common.js';
import { salesContactOutcomeSchema } from './customer-interaction.js';
import { customerContactChannelSchema, resolvedCustomerContactPreferenceSchema, } from './customer.js';
import { callDirectionSchema } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema } from './pagination.js';
export const cadenceConfidenceSchema = z.enum(['NONE', 'LOW', 'MEDIUM', 'HIGH']);
export const reorderCadenceStatusSchema = z.enum([
    'NO_ORDERS',
    'INSUFFICIENT_DATA',
    'ON_TRACK',
    'DUE_SOON',
    'LATE',
    'OVERDUE',
    'SEASONAL_PAUSE',
    'OPERATING_PAUSE',
    'ACTIVE_ORDER',
    'PLANNED_ORDER',
    'EXCLUDED',
]);
export const cadenceSourceSchema = z.enum([
    'INDIVIDUAL_HISTORY',
    'CUSTOMER_OVERRIDE',
    'CUSTOMER_GROUP_DEFAULT',
    'TENANT_DEFAULT',
    'SYSTEM_DEFAULT',
    'NONE',
]);
export const cadenceReasonSchema = z.enum([
    'NO_ORDER_HISTORY',
    'INSUFFICIENT_HISTORY_FALLBACK',
    'INDIVIDUAL_CADENCE',
    'CUSTOMER_OVERRIDE',
    'EXPECTED_DATE_EXCEEDED',
    'EXPECTED_SOON',
    'SEASONAL_NOT_EXPECTED',
    'CUSTOMER_OPERATING_PAUSE',
    'OPEN_ORDER_EXISTS',
    'PLANNED_ORDER_EXISTS',
    'CADENCE_EXCLUDED',
]);
const nullableDate = isoDateSchema.nullable();
const nullableNumber = z.number().finite().nullable();
export const reorderCadenceSchema = z.object({
    customerId: idSchema,
    evaluatedAt: isoDateTimeSchema,
    orderCount: z.number().int().min(0),
    intervalCount: z.number().int().min(0),
    usableIntervalCount: z.number().int().min(0),
    ignoredOrderIds: z.array(idSchema),
    ignoredOutlierIntervals: z.array(z.number().int().min(0)),
    lastOrderId: idSchema.nullable(),
    lastOrderDate: nullableDate,
    daysSinceLastOrder: z.number().int().min(0).nullable(),
    typicalIntervalDays: z.number().int().positive().nullable(),
    typicalIntervalRange: z
        .object({ minDays: z.number().int().positive(), maxDays: z.number().int().positive() })
        .nullable(),
    expectedNextOrderDate: nullableDate,
    deviationDays: z.number().int().nullable(),
    deviationRatio: nullableNumber,
    confidence: z.object({
        level: cadenceConfidenceSchema,
        score: z.number().min(0).max(1),
        sampleOrderCount: z.number().int().min(0),
        sampleIntervalCount: z.number().int().min(0),
    }),
    status: reorderCadenceStatusSchema,
    cadenceSource: cadenceSourceSchema,
    policyId: idSchema.nullable(),
    policyName: z.string().nullable(),
    usedFallback: z.boolean(),
    isSeasonal: z.boolean(),
    activeMonths: z.array(z.number().int().min(1).max(12)),
    operatingPause: z
        .object({ id: idSchema, startsOn: isoDateSchema, endsOn: isoDateSchema, reason: z.string() })
        .nullable(),
    activeOrder: z
        .object({ id: idSchema, number: z.string(), deliveryDate: nullableDate })
        .nullable(),
    plannedOrderDate: nullableDate,
    reasonCode: cadenceReasonSchema,
    reasonParams: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export const salesCadencePolicyScopeSchema = z.enum(['TENANT', 'CUSTOMER_GROUP']);
export const monthSchema = z.number().int().min(1).max(12);
export const salesCadencePolicySchema = z.object({
    id: idSchema,
    scope: salesCadencePolicyScopeSchema,
    scopeKey: z.string(),
    customerGroup: z.string().nullable(),
    name: z.string(),
    minOrderCount: z.number().int().min(2).max(50),
    defaultIntervalDays: z.number().int().min(1).max(3650),
    dueSoonDays: z.number().int().min(0).max(365),
    overdueTolerancePercent: z.number().int().min(0).max(500),
    lookbackOrderCount: z.number().int().min(3).max(250),
    activeMonths: z.array(monthSchema),
    isActive: z.boolean(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const salesCadencePolicyListResponseSchema = z.object({
    data: z.array(salesCadencePolicySchema),
});
export const salesCadencePolicyWriteSchema = z
    .object({
    scope: salesCadencePolicyScopeSchema,
    customerGroup: z.string().trim().min(1).max(120).nullish(),
    name: z.string().trim().min(1).max(160),
    minOrderCount: z.number().int().min(2).max(50).default(3),
    defaultIntervalDays: z.number().int().min(1).max(3650),
    dueSoonDays: z.number().int().min(0).max(365).default(7),
    overdueTolerancePercent: z.number().int().min(0).max(500).default(20),
    lookbackOrderCount: z.number().int().min(3).max(250).default(36),
    activeMonths: z.array(monthSchema).max(12).default([]),
    isActive: z.boolean().default(true),
})
    .superRefine((value, context) => {
    if (value.scope === 'TENANT' &&
        value.customerGroup !== null &&
        value.customerGroup !== undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customerGroup'],
            message: 'SALES_CADENCE_TENANT_GROUP_FORBIDDEN',
        });
    }
    if (value.scope === 'CUSTOMER_GROUP' &&
        (value.customerGroup === null || value.customerGroup === undefined)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customerGroup'],
            message: 'SALES_CADENCE_GROUP_REQUIRED',
        });
    }
    if (new Set(value.activeMonths).size !== value.activeMonths.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['activeMonths'],
            message: 'SALES_CADENCE_MONTHS_UNIQUE',
        });
    }
});
export const customerCadenceSettingsSchema = z.object({
    careIntervalDays: z.number().int().min(1).max(3650).nullable(),
    orderIntervalOverrideDays: z.number().int().min(1).max(3650).nullable(),
    plannedOrderDate: nullableDate,
    seasonalOrderMonths: z.array(monthSchema),
    cadenceExcluded: z.boolean(),
});
export const customerCadenceSettingsWriteSchema = z.object({
    careIntervalDays: z.number().int().min(1).max(3650).nullish(),
    orderIntervalOverrideDays: z.number().int().min(1).max(3650).nullish(),
    plannedOrderDate: isoDateSchema.nullish(),
    seasonalOrderMonths: z.array(monthSchema).max(12).default([]),
    cadenceExcluded: z.boolean().default(false),
});
export const cadenceEvaluationQuerySchema = z.object({
    evaluatedAt: isoDateTimeSchema.optional(),
});
export const operatingPauseSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    reason: z.string(),
    createdAt: isoDateTimeSchema,
});
export const operatingPauseWriteSchema = z
    .object({
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    reason: z.string().trim().min(1).max(500),
})
    .refine((value) => value.endsOn >= value.startsOn, {
    message: 'SALES_OPERATING_PAUSE_DATE_ORDER',
    path: ['endsOn'],
});
export const operatingPauseListResponseSchema = z.object({ data: z.array(operatingPauseSchema) });
export const SALES_RULE_TYPES = [
    'LAST_ORDER_UNUSUAL',
    'PERSONAL_CONTACT_OVERDUE',
    'EXPECTED_REORDER_MISSED',
    'NEW_CUSTOMER_NO_SECOND_ORDER',
    'PROSPECT_FOLLOW_UP_OVERDUE',
    'QUOTE_WITHOUT_RESPONSE',
    'REVENUE_DECLINE',
    'ORDER_FREQUENCY_DECLINE',
    'REGULAR_PRODUCT_MISSING',
    'PRODUCT_CATEGORY_MISSING',
    'PORTAL_CART_ABANDONED',
    'CALLBACK_WITHOUT_TASK',
    'CUSTOMER_UNASSIGNED',
    'COMMERCIAL_VALIDITY_EXPIRING',
    'OPEN_COMPLAINT_OR_RETURN',
    'CUSTOMER_BLOCKED_OR_AT_RISK',
    'OVERDUE_INVOICE_OR_DUNNING',
];
export const salesRuleTypeSchema = z.enum(SALES_RULE_TYPES);
export const salesPrioritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export const salesSuggestedActionSchema = z.enum([
    'CALL',
    'PLAN_CALLBACK',
    'CREATE_TASK',
    'RESOLVE_COMPLAINT',
    'REVIEW_RISK',
    'REVIEW_PAYMENT',
    'FOLLOW_UP_QUOTE',
    'CREATE_QUOTE',
    'REVIEW_CART',
    'ASSIGN_OWNER',
    'REVIEW_COMMERCIAL_TERMS',
    'OPEN_CUSTOMER',
]);
export const salesRecommendationStatusSchema = z.enum([
    'OPEN',
    'IN_PROGRESS',
    'SNOOZED',
    'COMPLETED',
    'STALE',
    'IRRELEVANT',
    'EXCLUDED',
]);
const criterionValueSchema = z.union([
    z.string().max(500),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(z.union([z.string().max(500), z.number().finite(), z.boolean()])).max(100),
]);
export const salesRuleConditionsSchema = z
    .object({
    customerIds: z.array(idSchema).max(500).default([]),
    customerGroups: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    prospectStatuses: z
        .array(z.enum(['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL_SENT', 'NEGOTIATION', 'WON', 'LOST']))
        .max(20)
        .default([]),
    industries: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    regions: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    salesTerritories: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    responsibleUserIds: z.array(idSchema).max(250).default([]),
    teamIds: z.array(idSchema).max(250).default([]),
    revenueClasses: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    riskStatuses: z
        .array(z.enum(['NORMAL', 'WATCHLIST', 'DELIVERY_LOCK', 'SALES_LOCK', 'COLLECTION', 'INSOLVENCY']))
        .max(10)
        .default([]),
    customFields: z.record(z.string().min(1).max(100), criterionValueSchema).default({}),
    weekdays: z.array(z.number().int().min(1).max(7)).max(7).default([]),
    activeFrom: isoDateSchema.nullable().default(null),
    activeUntil: isoDateSchema.nullable().default(null),
    minimumDataCount: z.number().int().min(0).max(10_000).default(0),
})
    .superRefine((value, context) => {
    if (value.activeFrom !== null &&
        value.activeUntil !== null &&
        value.activeUntil < value.activeFrom) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['activeUntil'],
            message: 'SALES_RULE_ACTIVE_DATE_ORDER',
        });
    }
});
export const salesRuleParametersSchema = z.object({
    thresholdDays: z.number().int().min(0).max(3650).default(90),
    minimumDeviationDays: z.number().int().min(0).max(3650).default(1),
    comparisonDays: z.number().int().min(7).max(730).default(90),
    declinePercent: z.number().min(0).max(100).default(25),
    minimumRevenue: z.number().min(0).max(1_000_000_000_000).default(0),
    minimumDeviationRatio: z.number().min(0).max(10).default(0.5),
    lookbackOrderCount: z.number().int().min(2).max(100).default(6),
    minimumOccurrences: z.number().int().min(2).max(100).default(3),
    missingCycles: z.number().int().min(1).max(20).default(2),
    thresholdHours: z.number().int().min(1).max(8760).default(48),
    leadDays: z.number().int().min(0).max(3650).default(14),
    attributionWindowDays: z.number().int().min(1).max(365).default(30),
    escalationAfterDays: z.number().int().min(1).max(3650).nullable().default(null),
    escalationPriority: salesPrioritySchema.nullable().default(null),
});
const salesRuleFieldsSchema = z.object({
    key: z
        .string()
        .trim()
        .regex(/^[a-z][a-z0-9:_-]{2,119}$/u, 'SALES_RULE_KEY_INVALID'),
    type: salesRuleTypeSchema,
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1_000).nullish(),
    isActive: z.boolean().default(false),
    testMode: z.boolean().default(true),
    priority: salesPrioritySchema.default('MEDIUM'),
    dueInDays: z.number().int().min(0).max(3650).default(3),
    expiresAfterDays: z.number().int().min(1).max(3650).nullish(),
    parameters: salesRuleParametersSchema.default({}),
    conditions: salesRuleConditionsSchema.default({}),
    suggestedAction: salesSuggestedActionSchema,
    defaultResponsibleId: idSchema.nullish(),
    defaultTeamId: idSchema.nullish(),
    changeReason: z.string().trim().min(3).max(500),
});
export const salesRuleCreateSchema = salesRuleFieldsSchema;
export const salesRuleUpdateSchema = salesRuleFieldsSchema.partial().extend({
    changeReason: z.string().trim().min(3).max(500),
    expectedVersion: z.number().int().positive().optional(),
});
export const salesRuleSchema = salesRuleFieldsSchema.omit({ changeReason: true }).extend({
    id: idSchema,
    description: z.string().nullable(),
    expiresAfterDays: z.number().int().nullable(),
    defaultResponsibleId: idSchema.nullable(),
    defaultTeamId: idSchema.nullable(),
    version: z.number().int().positive(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const salesRuleListQuerySchema = paginationQuerySchema.extend({
    type: salesRuleTypeSchema.optional(),
    active: z.coerce.boolean().optional(),
    testMode: z.coerce.boolean().optional(),
});
export const salesRuleListResponseSchema = z.object({
    data: z.array(salesRuleSchema),
    meta: paginationMetaSchema,
});
export const salesRuleVersionSchema = z.object({
    id: idSchema,
    ruleId: idSchema,
    version: z.number().int().positive(),
    snapshot: z.record(z.string(), z.unknown()),
    changeReason: z.string().nullable(),
    changedByUserId: idSchema.nullable(),
    changedAt: isoDateTimeSchema,
});
export const salesRuleVersionListResponseSchema = z.object({
    data: z.array(salesRuleVersionSchema),
});
export const salesRuleRestoreSchema = z.object({
    version: z.number().int().positive(),
    expectedVersion: z.number().int().positive(),
    changeReason: z.string().trim().min(3).max(500),
});
export const salesPriorityFactorSchema = z.object({
    code: z.string().min(1).max(120),
    impact: z.number().int().min(-3).max(3),
    facts: z.record(z.string(), z.unknown()),
});
export const salesConflictSchema = z.object({
    code: z.string().min(1).max(120),
    severity: salesPrioritySchema,
    facts: z.record(z.string(), z.unknown()),
});
export const salesRecommendationCandidateSchema = z.object({
    customerId: idSchema,
    ruleId: idSchema,
    ruleVersion: z.number().int().positive(),
    type: salesRuleTypeSchema,
    priority: salesPrioritySchema,
    priorityExplanation: z.array(salesPriorityFactorSchema),
    reasonCode: z.string().min(1).max(160),
    facts: z.record(z.string(), z.unknown()),
    generatedAt: isoDateTimeSchema,
    dueAt: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema.nullable(),
    assignedUserId: idSchema.nullable(),
    assignedTeamId: idSchema.nullable(),
    suggestedAction: salesSuggestedActionSchema,
    deduplicationKey: z.string().min(16).max(128),
    subjectKey: z.string().min(1).max(240),
    conflicts: z.array(salesConflictSchema),
});
export const salesRecommendationCandidateListSchema = z.object({
    data: z.array(salesRecommendationCandidateSchema),
});
export const salesRuleSimulationRequestSchema = z.object({
    evaluatedAt: isoDateTimeSchema.optional(),
    sampleSize: z.number().int().min(1).max(20).default(5),
    overrides: salesRuleUpdateSchema.omit({ changeReason: true, expectedVersion: true }).optional(),
});
export const salesRuleSimulationExampleSchema = salesRecommendationCandidateSchema.extend({
    customerNumber: z.string(),
    customerName: z.string(),
});
export const salesRuleSimulationAssignmentSchema = z.object({
    assignedUserId: idSchema.nullable(),
    assignedUserName: z.string().nullable(),
    assignedTeamId: idSchema.nullable(),
    assignedTeamName: z.string().nullable(),
    count: z.number().int().nonnegative(),
});
export const salesRuleSimulationSchema = z.object({
    ruleId: idSchema,
    ruleVersion: z.number().int().positive(),
    evaluatedAt: isoDateTimeSchema,
    totalCustomers: z.number().int().nonnegative(),
    affectedCustomers: z.number().int().nonnegative(),
    estimatedTasks: z.number().int().nonnegative(),
    excludedCustomers: z.number().int().nonnegative(),
    outsideTargetGroup: z.number().int().nonnegative(),
    conflictCustomers: z.number().int().nonnegative(),
    examples: z.array(salesRuleSimulationExampleSchema),
    assignmentDistribution: z.array(salesRuleSimulationAssignmentSchema),
    isTestMode: z.literal(true),
});
export const salesRuleEvaluationSchema = z.object({
    evaluatedAt: isoDateTimeSchema.optional(),
    includeTestMode: z.boolean().default(true),
});
export const salesRecommendationSchema = salesRecommendationCandidateSchema.extend({
    id: idSchema,
    status: salesRecommendationStatusSchema,
    snoozedUntil: isoDateTimeSchema.nullable(),
    resolutionReason: z.string().nullable(),
    resolvedAt: isoDateTimeSchema.nullable(),
    resolvedById: idSchema.nullable(),
    lastSeenAt: isoDateTimeSchema,
    createdByRunId: idSchema.nullable(),
    lastSeenRunId: idSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const salesCockpitCategorySchema = z.enum([
    'ALL',
    'DUE_NOW',
    'MISSED_REORDER',
    'REVENUE_DECLINE',
    'OPEN_QUOTES',
    'NEW_CUSTOMERS',
    'REACTIVATION',
    'PROSPECTS',
    'BLOCKED',
]);
export const salesCockpitViewModeSchema = z.enum(['LIST', 'CARDS']);
export const salesCockpitScopeSchema = z.enum(['OWN', 'TEAM', 'ALL']);
const csvRecommendationStatusSchema = z.preprocess((value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value), z.array(salesRecommendationStatusSchema).max(7));
const csvPrioritySchema = z.preprocess((value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value), z.array(salesPrioritySchema).max(4));
const csvRuleTypeSchema = z.preprocess((value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value), z.array(salesRuleTypeSchema).max(17));
export const salesCockpitRecommendationListQuerySchema = paginationQuerySchema.extend({
    scope: salesCockpitScopeSchema.default('OWN'),
    category: salesCockpitCategorySchema.default('ALL'),
    status: csvRecommendationStatusSchema.default(['OPEN', 'IN_PROGRESS']),
    priority: csvPrioritySchema.optional(),
    type: csvRuleTypeSchema.optional(),
    assignedUserId: idSchema.optional(),
    assignedTeamId: idSchema.optional(),
    dueBefore: isoDateTimeSchema.optional(),
    dueAfter: isoDateTimeSchema.optional(),
    sort: z
        .enum(['priority:desc', 'dueAt:asc', 'dueAt:desc', 'generatedAt:desc'])
        .default('priority:desc'),
});
export const salesCockpitRecommendationSchema = salesRecommendationSchema.extend({
    customerNumber: z.string(),
    customerName: z.string(),
    customerPhone: z.string().nullable(),
    isProspect: z.boolean(),
    ownerUserId: idSchema.nullable(),
    ownerUserName: z.string().nullable(),
    assignedUserName: z.string().nullable(),
    assignedTeamName: z.string().nullable(),
    lastOrderDate: isoDateSchema.nullable(),
    lastPersonalContactAt: isoDateTimeSchema.nullable(),
    typicalIntervalDays: z.number().int().positive().nullable(),
    typicalIntervalRange: z
        .object({ minDays: z.number().int().positive(), maxDays: z.number().int().positive() })
        .nullable(),
    cadenceStatus: reorderCadenceStatusSchema,
    activeTaskId: idSchema.nullable(),
});
export const salesCockpitRecommendationListResponseSchema = z.object({
    data: z.array(salesCockpitRecommendationSchema),
    meta: paginationMetaSchema,
});
export const salesCockpitSummarySchema = z.object({
    dueNow: z.number().int().nonnegative(),
    overdueTasks: z.number().int().nonnegative(),
    todayCalls: z.number().int().nonnegative(),
    upcomingFollowUps: z.number().int().nonnegative(),
    missedReorders: z.number().int().nonnegative(),
    revenueDeclines: z.number().int().nonnegative(),
    openQuotes: z.number().int().nonnegative(),
    newCustomers: z.number().int().nonnegative(),
    reactivationCandidates: z.number().int().nonnegative(),
    prospectsWithoutFollowUp: z.number().int().nonnegative(),
    blockedCases: z.number().int().nonnegative(),
    recentlyCompleted: z.number().int().nonnegative(),
});
export const salesCockpitSummaryQuerySchema = z.object({
    scope: salesCockpitScopeSchema.default('OWN'),
});
export const salesAssistantSavedViewFiltersSchema = z.object({
    scope: salesCockpitScopeSchema.default('OWN'),
    category: salesCockpitCategorySchema.default('ALL'),
    status: z.array(salesRecommendationStatusSchema).max(7).default(['OPEN', 'IN_PROGRESS']),
    priority: z.array(salesPrioritySchema).max(4).default([]),
    type: z.array(salesRuleTypeSchema).max(17).default([]),
    q: z.string().trim().max(200).default(''),
    sort: z
        .enum(['priority:desc', 'dueAt:asc', 'dueAt:desc', 'generatedAt:desc'])
        .default('priority:desc'),
});
export const salesAssistantSavedViewWriteSchema = z.object({
    name: z.string().trim().min(1).max(100),
    viewMode: salesCockpitViewModeSchema.default('CARDS'),
    filters: salesAssistantSavedViewFiltersSchema,
    isDefault: z.boolean().default(false),
});
export const salesAssistantSavedViewSchema = salesAssistantSavedViewWriteSchema.extend({
    id: idSchema,
    position: z.number().int().nonnegative(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const salesAssistantSavedViewListSchema = z.object({
    data: z.array(salesAssistantSavedViewSchema),
});
export const salesBulkRecommendationAssignSchema = z
    .object({
    recommendationIds: z.array(idSchema).min(1).max(100),
    assignedUserId: idSchema.nullish(),
    assignedTeamId: idSchema.nullish(),
})
    .refine((value) => (value.assignedUserId !== null && value.assignedUserId !== undefined) ||
    (value.assignedTeamId !== null && value.assignedTeamId !== undefined), { message: 'SALES_BULK_ASSIGN_TARGET_REQUIRED' });
export const salesBulkRecommendationSnoozeSchema = z.object({
    recommendationIds: z.array(idSchema).min(1).max(100),
    snoozedUntil: isoDateTimeSchema,
    reason: z.string().trim().min(1).max(500),
});
export const salesBulkOperationResultSchema = z.object({ updated: z.number().int().nonnegative() });
export const salesAssigneeSchema = z.object({ id: idSchema, name: z.string() });
export const salesAssigneeListSchema = z.object({ data: z.array(salesAssigneeSchema) });
export const salesAssistantSettingsSchema = z.object({
    enabled: z.boolean(),
    intervalMinutes: z.number().int().min(15).max(10_080),
    batchSize: z.number().int().min(10).max(500),
    attributionWindowDays: z.number().int().min(1).max(365),
    workdays: z.array(z.number().int().min(1).max(7)).min(1).max(7),
    quietHoursStart: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u),
    quietHoursEnd: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u),
    nextRunAt: isoDateTimeSchema,
    lastRunAt: isoDateTimeSchema.nullable(),
    leaseOwnerRunId: idSchema.nullable(),
    leaseExpiresAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
});
export const salesAssistantSettingsWriteSchema = z.object({
    enabled: z.boolean(),
    intervalMinutes: z.number().int().min(15).max(10_080),
    batchSize: z.number().int().min(10).max(500),
    attributionWindowDays: z.number().int().min(1).max(365).default(30),
    workdays: z
        .array(z.number().int().min(1).max(7))
        .min(1)
        .max(7)
        .refine((items) => new Set(items).size === items.length, {
        message: 'SALES_ASSISTANT_WORKDAYS_UNIQUE',
    })
        .default([1, 2, 3, 4, 5]),
    quietHoursStart: z
        .string()
        .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u)
        .default('18:00'),
    quietHoursEnd: z
        .string()
        .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u)
        .default('08:00'),
});
export const salesRecommendationRunStatusSchema = z.enum([
    'QUEUED',
    'RUNNING',
    'COMPLETED',
    'FAILED',
]);
export const salesRecommendationRunTriggerSchema = z.enum(['SCHEDULED', 'MANUAL', 'RECOVERY']);
export const salesRecommendationRunSchema = z.object({
    id: idSchema,
    trigger: salesRecommendationRunTriggerSchema,
    status: salesRecommendationRunStatusSchema,
    requestedById: idSchema.nullable(),
    idempotencyKey: z.string(),
    evaluatedAt: isoDateTimeSchema,
    totalCustomers: z.number().int().nonnegative(),
    processedCustomers: z.number().int().nonnegative(),
    candidatesFound: z.number().int().nonnegative(),
    recommendationsCreated: z.number().int().nonnegative(),
    recommendationsUpdated: z.number().int().nonnegative(),
    recommendationsStale: z.number().int().nonnegative(),
    checkpointCustomerId: idSchema.nullable(),
    attemptCount: z.number().int().nonnegative(),
    retryable: z.boolean(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    startedAt: isoDateTimeSchema.nullable(),
    heartbeatAt: isoDateTimeSchema.nullable(),
    nextRetryAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const salesRecommendationRunRequestSchema = z.object({
    idempotencyKey: z.string().trim().min(8).max(160),
    evaluatedAt: isoDateTimeSchema.optional(),
});
export const salesRecommendationRunDispatchSchema = z.object({
    run: salesRecommendationRunSchema,
    queueAccepted: z.boolean(),
});
export const salesRecommendationRunListQuerySchema = paginationQuerySchema.extend({
    status: salesRecommendationRunStatusSchema.optional(),
    trigger: salesRecommendationRunTriggerSchema.optional(),
});
export const salesRecommendationRunListResponseSchema = z.object({
    data: z.array(salesRecommendationRunSchema),
    meta: paginationMetaSchema,
});
export const customerCareCaseTypeSchema = z.enum(['COMPLAINT', 'RETURN']);
export const customerCareCaseStatusSchema = z.enum(['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']);
export const customerCareCaseSeveritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export const customerCareCaseSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    type: customerCareCaseTypeSchema,
    status: customerCareCaseStatusSchema,
    severity: customerCareCaseSeveritySchema,
    subject: z.string(),
    note: z.string().nullable(),
    assignedUserId: idSchema.nullable(),
    openedAt: isoDateTimeSchema,
    resolvedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const customerCareCaseWriteSchema = z.object({
    type: customerCareCaseTypeSchema,
    status: customerCareCaseStatusSchema.default('OPEN'),
    severity: customerCareCaseSeveritySchema.default('MEDIUM'),
    subject: z.string().trim().min(1).max(240),
    note: z.string().trim().max(10_000).nullish(),
    assignedUserId: idSchema.nullish(),
    openedAt: isoDateTimeSchema.default(() => new Date().toISOString()),
});
export const customerCareCaseUpdateSchema = customerCareCaseWriteSchema.partial();
export const customerCareCaseListResponseSchema = z.object({
    data: z.array(customerCareCaseSchema),
});
export const teamMemberRoleSchema = z.enum(['MEMBER', 'LEAD']);
export const salesTeamMemberSchema = z.object({
    id: idSchema,
    userId: idSchema,
    role: teamMemberRoleSchema,
    userName: z.string(),
});
export const salesTeamSchema = z.object({
    id: idSchema,
    name: z.string(),
    isActive: z.boolean(),
    managerId: idSchema.nullable(),
    members: z.array(salesTeamMemberSchema),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const salesTeamListResponseSchema = z.object({ data: z.array(salesTeamSchema) });
export const salesTeamWriteSchema = z.object({
    name: z.string().trim().min(1).max(160),
    isActive: z.boolean().default(true),
    managerId: idSchema.nullish(),
    members: z
        .array(z.object({ userId: idSchema, role: teamMemberRoleSchema.default('MEMBER') }))
        .max(250)
        .default([])
        .refine((entries) => new Set(entries.map((entry) => entry.userId)).size === entries.length, {
        message: 'SALES_TEAM_MEMBERS_UNIQUE',
    }),
});
export const delegationRuleSchema = z.object({
    id: idSchema,
    absentUserId: idSchema,
    substituteUserId: idSchema.nullable(),
    substituteTeamId: idSchema.nullable(),
    startsAt: isoDateTimeSchema,
    endsAt: isoDateTimeSchema,
    reason: z.string().nullable(),
    isActive: z.boolean(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const delegationRuleWriteSchema = z
    .object({
    absentUserId: idSchema,
    substituteUserId: idSchema.nullish(),
    substituteTeamId: idSchema.nullish(),
    startsAt: isoDateTimeSchema,
    endsAt: isoDateTimeSchema,
    reason: z.string().trim().max(500).nullish(),
    isActive: z.boolean().default(true),
})
    .superRefine((value, context) => {
    const destinations = [value.substituteUserId, value.substituteTeamId].filter((entry) => entry !== null && entry !== undefined);
    if (destinations.length !== 1) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['substituteUserId'],
            message: 'SALES_DELEGATION_ONE_DESTINATION_REQUIRED',
        });
    }
    if (value.substituteUserId === value.absentUserId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['substituteUserId'],
            message: 'SALES_DELEGATION_SELF_FORBIDDEN',
        });
    }
    if (new Date(value.endsAt).getTime() <= new Date(value.startsAt).getTime()) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endsAt'],
            message: 'SALES_DELEGATION_DATE_ORDER',
        });
    }
});
export const delegationRuleListResponseSchema = z.object({ data: z.array(delegationRuleSchema) });
export const salesTaskStatusSchema = z.enum([
    'OPEN',
    'IN_PROGRESS',
    'WAITING',
    'COMPLETED',
    'CANCELLED',
]);
export const salesTaskActivityTypeSchema = z.enum([
    'CALL',
    'CALLBACK',
    'VISIT',
    'MEETING',
    'EMAIL',
    'QUOTE',
    'FOLLOW_UP',
    'COMPLAINT',
    'OTHER',
]);
export const salesTaskHistoryActionSchema = z.enum([
    'CREATED',
    'UPDATED',
    'ASSIGNED',
    'REASSIGNED',
    'CLAIMED',
    'RELEASED',
    'COMMENTED',
    'CHECKLIST_UPDATED',
    'ATTACHMENT_LINKED',
    'COMPLETED',
    'CANCELLED',
    'REOPENED',
    'OUTCOME_RECORDED',
]);
export const salesTaskOutcomeDataSchema = z.object({
    contactId: idSchema.nullable(),
    direction: callDirectionSchema.nullable(),
    durationSeconds: z.number().int().nonnegative().nullable(),
    note: z.string().nullable(),
    nextContactAt: isoDateTimeSchema.nullable(),
    expectedOrderDate: isoDateSchema.nullable(),
    expectedRevenue: moneySchema.nullable(),
    requestedProductIds: z.array(idSchema),
    requestedCategoryIds: z.array(idSchema),
    newContactId: idSchema.nullable(),
    noNeedReason: z.string().nullable(),
    lossReason: z.string().nullable(),
    closedUntil: isoDateSchema.nullable(),
    followUpTaskId: idSchema.nullable(),
    createdQuoteId: idSchema.nullable(),
    createdCaseId: idSchema.nullable(),
});
export const salesTaskChecklistItemSchema = z.object({
    id: idSchema,
    title: z.string(),
    position: z.number().int().nonnegative(),
    isCompleted: z.boolean(),
    completedAt: isoDateTimeSchema.nullable(),
    completedByUserId: idSchema.nullable(),
    createdByUserId: idSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const salesTaskCommentSchema = z.object({
    id: idSchema,
    body: z.string(),
    createdByUserId: idSchema,
    createdByName: z.string(),
    createdAt: isoDateTimeSchema,
});
export const salesTaskHistorySchema = z.object({
    id: idSchema,
    action: salesTaskHistoryActionSchema,
    changes: z.record(z.string(), z.unknown()),
    actorUserId: idSchema,
    actorName: z.string(),
    occurredAt: isoDateTimeSchema,
});
export const salesTaskSummarySchema = z.object({
    id: idSchema,
    customerId: idSchema,
    customerNumber: z.string(),
    customerName: z.string(),
    customerPhone: z.string().nullable(),
    contactPreference: resolvedCustomerContactPreferenceSchema,
    recommendationId: idSchema.nullable(),
    parentTaskId: idSchema.nullable(),
    title: z.string(),
    reason: z.string(),
    assignedUserId: idSchema.nullable(),
    assignedUserName: z.string().nullable(),
    assignedTeamId: idSchema.nullable(),
    assignedTeamName: z.string().nullable(),
    delegatedFromUserId: idSchema.nullable(),
    priority: salesPrioritySchema,
    dueAt: isoDateTimeSchema,
    reminderAt: isoDateTimeSchema.nullable(),
    status: salesTaskStatusSchema,
    activityType: salesTaskActivityTypeSchema,
    claimedById: idSchema.nullable(),
    claimedByName: z.string().nullable(),
    claimedAt: isoDateTimeSchema.nullable(),
    claimExpiresAt: isoDateTimeSchema.nullable(),
    completedAt: isoDateTimeSchema.nullable(),
    completedById: idSchema.nullable(),
    resultSummary: z.string().nullable(),
    outcome: salesContactOutcomeSchema.nullable(),
    outcomeOccurredAt: isoDateTimeSchema.nullable(),
    outcomeData: salesTaskOutcomeDataSchema.nullable(),
    checklistTotal: z.number().int().nonnegative(),
    checklistCompleted: z.number().int().nonnegative(),
    commentCount: z.number().int().nonnegative(),
    attachmentCount: z.number().int().nonnegative(),
    createdById: idSchema,
    updatedById: idSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const salesTaskSchema = salesTaskSummarySchema.extend({
    attachmentIds: z.array(idSchema),
    checklist: z.array(salesTaskChecklistItemSchema),
    comments: z.array(salesTaskCommentSchema),
    history: z.array(salesTaskHistorySchema),
});
const nullableIdInput = idSchema.nullish();
export const salesTaskCreateSchema = z
    .object({
    customerId: idSchema,
    recommendationId: nullableIdInput,
    parentTaskId: nullableIdInput,
    title: z.string().trim().min(1).max(240),
    reason: z.string().trim().min(1).max(10_000),
    assignedUserId: nullableIdInput,
    assignedTeamId: nullableIdInput,
    priority: salesPrioritySchema.default('MEDIUM'),
    dueAt: isoDateTimeSchema,
    reminderAt: isoDateTimeSchema.nullish(),
    activityType: salesTaskActivityTypeSchema,
    checklist: z.array(z.string().trim().min(1).max(240)).max(50).default([]),
    attachmentIds: z.array(idSchema).max(20).default([]),
})
    .superRefine((value, context) => {
    if ((value.assignedUserId === null || value.assignedUserId === undefined) &&
        (value.assignedTeamId === null || value.assignedTeamId === undefined) &&
        (value.recommendationId === null || value.recommendationId === undefined)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assignedUserId'],
            message: 'SALES_TASK_ASSIGNEE_REQUIRED',
        });
    }
    if (value.reminderAt !== null &&
        value.reminderAt !== undefined &&
        new Date(value.reminderAt).getTime() > new Date(value.dueAt).getTime()) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reminderAt'],
            message: 'SALES_TASK_REMINDER_AFTER_DUE',
        });
    }
});
export const salesTaskUpdateSchema = z
    .object({
    title: z.string().trim().min(1).max(240).optional(),
    reason: z.string().trim().min(1).max(10_000).optional(),
    assignedUserId: nullableIdInput,
    assignedTeamId: nullableIdInput,
    priority: salesPrioritySchema.optional(),
    dueAt: isoDateTimeSchema.optional(),
    reminderAt: isoDateTimeSchema.nullish(),
    status: salesTaskStatusSchema.optional(),
    activityType: salesTaskActivityTypeSchema.optional(),
    resultSummary: z.string().trim().max(10_000).nullish(),
})
    .refine((value) => Object.values(value).some((entry) => entry !== undefined), {
    message: 'SALES_TASK_UPDATE_REQUIRED',
});
export const salesTaskClaimSchema = z.object({
    leaseMinutes: z.number().int().min(5).max(240).default(30),
});
const salesTaskFollowUpWriteSchema = z.object({
    title: z.string().trim().min(1).max(240).optional(),
    assignedUserId: idSchema.nullish(),
    assignedTeamId: idSchema.nullish(),
    priority: salesPrioritySchema.optional(),
    reminderAt: isoDateTimeSchema.nullish(),
    activityType: salesTaskActivityTypeSchema.optional(),
});
export const salesTaskOutcomeWriteSchema = z
    .object({
    idempotencyKey: z.string().trim().min(8).max(160).optional(),
    outcome: salesContactOutcomeSchema,
    occurredAt: isoDateTimeSchema.default(() => new Date().toISOString()),
    contactId: idSchema.nullish(),
    direction: callDirectionSchema.nullish(),
    durationSeconds: z.number().int().min(0).max(86_400).nullish(),
    note: z.string().trim().max(10_000).nullish(),
    nextContactAt: isoDateTimeSchema.nullish(),
    expectedOrderDate: isoDateSchema.nullish(),
    expectedRevenue: moneySchema.nullish(),
    requestedProductIds: z.array(idSchema).max(100).default([]),
    requestedCategoryIds: z.array(idSchema).max(50).default([]),
    newContactId: idSchema.nullish(),
    noNeedReason: z.string().trim().max(1_000).nullish(),
    lossReason: z.string().trim().max(1_000).nullish(),
    closedUntil: isoDateSchema.nullish(),
    followUp: salesTaskFollowUpWriteSchema.nullish(),
    createQuote: z
        .object({ validDays: z.number().int().min(1).max(QUOTE_VALIDITY_DAYS_MAX).optional() })
        .nullish(),
    createComplaint: z
        .object({
        subject: z.string().trim().min(1).max(240),
        severity: customerCareCaseSeveritySchema.default('MEDIUM'),
    })
        .nullish(),
})
    .superRefine((value, context) => {
    const requireField = (condition, field, code) => {
        if (condition && (value[field] === null || value[field] === undefined)) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: code });
        }
    };
    requireField(['CALLBACK_REQUESTED', 'CALLBACK_SCHEDULED', 'CONTACT_LATER'].includes(value.outcome), 'nextContactAt', 'SALES_OUTCOME_NEXT_CONTACT_REQUIRED');
    requireField(value.outcome === 'ORDER_EXPECTED', 'expectedOrderDate', 'SALES_OUTCOME_EXPECTED_ORDER_REQUIRED');
    requireField(value.outcome === 'CONTACT_CHANGED', 'newContactId', 'SALES_OUTCOME_NEW_CONTACT_REQUIRED');
    requireField(value.outcome === 'NO_CURRENT_NEED', 'noNeedReason', 'SALES_OUTCOME_NO_NEED_REASON_REQUIRED');
    requireField(value.outcome === 'CUSTOMER_LOST', 'lossReason', 'SALES_OUTCOME_LOSS_REASON_REQUIRED');
    requireField(value.outcome === 'TEMPORARILY_CLOSED', 'closedUntil', 'SALES_OUTCOME_CLOSED_UNTIL_REQUIRED');
    requireField(value.outcome === 'COMPLAINT_OR_PROBLEM', 'createComplaint', 'SALES_OUTCOME_COMPLAINT_REQUIRED');
    requireField(value.outcome === 'OTHER', 'note', 'SALES_OUTCOME_NOTE_REQUIRED');
    requireField(value.followUp !== null && value.followUp !== undefined, 'nextContactAt', 'SALES_OUTCOME_FOLLOW_UP_DATE_REQUIRED');
    if (value.nextContactAt !== null &&
        value.nextContactAt !== undefined &&
        new Date(value.nextContactAt).getTime() <= new Date(value.occurredAt).getTime()) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nextContactAt'],
            message: 'SALES_OUTCOME_NEXT_CONTACT_FUTURE_REQUIRED',
        });
    }
    if (value.closedUntil !== null &&
        value.closedUntil !== undefined &&
        value.closedUntil < value.occurredAt.slice(0, 10)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['closedUntil'],
            message: 'SALES_OUTCOME_CLOSED_UNTIL_ORDER',
        });
    }
});
export const salesTaskCommentWriteSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
});
export const salesTaskChecklistWriteSchema = z.object({
    title: z.string().trim().min(1).max(240),
    position: z.number().int().min(0).max(999).optional(),
});
export const salesTaskChecklistUpdateSchema = z.object({
    title: z.string().trim().min(1).max(240).optional(),
    position: z.number().int().min(0).max(999).optional(),
    isCompleted: z.boolean().optional(),
});
const csvTaskStatusSchema = z.preprocess((value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value), z.array(salesTaskStatusSchema).max(5));
export const salesTaskListQuerySchema = paginationQuerySchema.extend({
    taskId: idSchema.optional(),
    scope: z.enum(['OWN', 'TEAM', 'ALL']).default('OWN'),
    status: csvTaskStatusSchema.optional(),
    customerId: idSchema.optional(),
    assignedUserId: idSchema.optional(),
    assignedTeamId: idSchema.optional(),
    dueBefore: isoDateTimeSchema.optional(),
    dueAfter: isoDateTimeSchema.optional(),
    activityType: salesTaskActivityTypeSchema.optional(),
    sort: z.enum(['dueAt:asc', 'dueAt:desc', 'priority:desc', 'createdAt:desc']).default('dueAt:asc'),
});
export const salesTaskListResponseSchema = z.object({
    data: z.array(salesTaskSummarySchema),
    meta: paginationMetaSchema,
});
export const salesRecommendationSnoozeSchema = z.object({
    snoozedUntil: isoDateTimeSchema,
    reason: z.string().trim().min(1).max(500),
});
export const salesRecommendationDecisionSchema = z.object({
    reason: z.string().trim().min(1).max(1_000),
});
export const customerCareStatusSchema = z.enum([
    'NEEDS_ATTENTION',
    'PLANNED',
    'ON_TRACK',
    'BLOCKED',
    'EXCLUDED',
    'INSUFFICIENT_DATA',
]);
export const customerCareInsightRecommendationSchema = z.object({
    id: idSchema,
    type: salesRuleTypeSchema,
    priority: salesPrioritySchema,
    dueAt: isoDateTimeSchema,
    suggestedAction: salesSuggestedActionSchema,
    status: salesRecommendationStatusSchema,
    conflicts: z.array(salesConflictSchema),
});
export const customerCareInsightTaskSchema = z.object({
    id: idSchema,
    title: z.string(),
    activityType: salesTaskActivityTypeSchema,
    priority: salesPrioritySchema,
    dueAt: isoDateTimeSchema,
    status: salesTaskStatusSchema,
});
export const customerCareInsightSchema = z.object({
    customerId: idSchema,
    status: customerCareStatusSchema,
    cadence: reorderCadenceSchema,
    lastOrderDate: isoDateSchema.nullable(),
    lastPersonalContactAt: isoDateTimeSchema.nullable(),
    lastSuccessfulPhoneContactAt: isoDateTimeSchema.nullable(),
    nextPlannedContactAt: isoDateTimeSchema.nullable(),
    owner: z.object({ id: idSchema, name: z.string() }).nullable(),
    careIntervalDays: z.number().int().positive().nullable(),
    cadenceExcluded: z.boolean(),
    openRecommendationCount: z.number().int().nonnegative(),
    openTaskCount: z.number().int().nonnegative(),
    recommendations: z.array(customerCareInsightRecommendationSchema),
    tasks: z.array(customerCareInsightTaskSchema),
    generalContactBlocked: z.boolean(),
    marketingBlocked: z.boolean(),
    preferredContactChannel: customerContactChannelSchema.nullable(),
    contactPreference: resolvedCustomerContactPreferenceSchema,
});
export const salesCallPreparationFactCodes = [
    'LAST_ORDER',
    'REORDER_OVERDUE',
    'REORDER_EXPECTED',
    'REORDER_INSUFFICIENT_DATA',
    'SEASONAL_PAUSE',
    'TYPICAL_PRODUCT_CATEGORY',
    'MISSING_REGULAR_PRODUCT',
    'MISSING_PRODUCT_CATEGORY',
    'OPEN_QUOTE',
    'OPEN_TASK',
    'RECENT_CONTACT',
    'OPEN_COMPLAINT',
    'CUSTOMER_RISK',
    'DELIVERY_BLOCK',
    'SALES_BLOCK',
    'OVERDUE_INVOICE',
    'CONTACT_BLOCK',
    'MARKETING_BLOCK',
];
export const salesCallPreparationFactCodeSchema = z.enum(salesCallPreparationFactCodes);
const salesCallPreparationParamSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const salesCallPreparationFactSchema = z.object({
    id: z.string().min(1).max(240),
    code: salesCallPreparationFactCodeSchema,
    params: z.record(z.string(), salesCallPreparationParamSchema),
    occurredAt: isoDateTimeSchema.nullable(),
    entityType: z.enum(['DOCUMENT', 'TASK', 'ACTIVITY', 'CASE', 'CUSTOMER']).nullable(),
    entityId: idSchema.nullable(),
    isSafetyRelevant: z.boolean(),
});
export const salesCallPreparationSchema = z.object({
    customerId: idSchema,
    generatedAt: isoDateTimeSchema,
    source: z.enum(['OLLAMA', 'DETERMINISTIC_FALLBACK']),
    ollamaStatus: z.enum(['SUCCESS', 'DISABLED', 'UNAVAILABLE', 'TIMEOUT', 'INVALID_RESPONSE']),
    isAiGenerated: z.boolean(),
    model: z.string().nullable(),
    facts: z.array(salesCallPreparationFactSchema),
    topicIds: z.array(z.string()).max(3).default([]),
    ruleVersion: z.string().default('call-preparation-2'),
    lastPersonalContactAt: isoDateTimeSchema.nullable().default(null),
    periodFrom: isoDateSchema.nullable().default(null),
    periodTo: isoDateSchema.nullable().default(null),
    contactPreference: resolvedCustomerContactPreferenceSchema.optional(),
    contacts: z
        .array(z.object({
        id: idSchema,
        name: z.string(),
        phone: z.string().nullable(),
        language: z.string(),
        responsibilities: z.array(z.object({
            id: idSchema,
            name: z.string(),
            translations: z.record(z.string()),
            purpose: z.string().nullable(),
            isActive: z.boolean(),
        })),
        topicIds: z.array(z.string()),
        preference: resolvedCustomerContactPreferenceSchema,
    }))
        .default([]),
    suggestions: z
        .array(z.object({ factId: z.string(), text: z.string().max(500) }))
        .max(3)
        .default([]),
});
export const customerCareInsightQuerySchema = z.object({
    scope: salesCockpitScopeSchema.default('OWN'),
});
export const customerSalesRuleExclusionSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    ruleType: salesRuleTypeSchema,
    reason: z.string(),
    createdById: idSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const customerSalesRuleExclusionListSchema = z.object({
    data: z.array(customerSalesRuleExclusionSchema),
});
export const customerSalesRuleExclusionWriteSchema = z.object({
    ruleTypes: z
        .array(salesRuleTypeSchema)
        .max(17)
        .refine((items) => new Set(items).size === items.length, {
        message: 'SALES_RULE_EXCLUSIONS_UNIQUE',
    }),
    reason: z.string().trim().min(1).max(1_000),
});
export const salesAssistantAnalyticsScopeSchema = z.enum(['OWN', 'TEAM']);
export const salesAssistantAnalyticsQuerySchema = paginationQuerySchema
    .extend({
    scope: salesAssistantAnalyticsScopeSchema.default('OWN'),
    dateFrom: isoDateSchema,
    dateTo: isoDateSchema,
    teamId: idSchema.optional(),
    userId: idSchema.optional(),
})
    .superRefine((value, context) => {
    const from = Date.parse(`${value.dateFrom}T00:00:00.000Z`);
    const to = Date.parse(`${value.dateTo}T00:00:00.000Z`);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dateTo'],
            message: 'SALES_ANALYTICS_DATE_ORDER',
        });
        return;
    }
    if ((to - from) / 86_400_000 > 366) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dateTo'],
            message: 'SALES_ANALYTICS_RANGE_TOO_LARGE',
        });
    }
});
export const salesAssistantAnalyticsMetricsSchema = z.object({
    recommendationsGenerated: z.number().int().nonnegative(),
    recommendationsProcessed: z.number().int().nonnegative(),
    overdueTasks: z.number().int().nonnegative(),
    averageResponseHours: z.number().nonnegative().nullable(),
    contactAttempts: z.number().int().nonnegative(),
    successfulContacts: z.number().int().nonnegative(),
    plannedCallbacks: z.number().int().nonnegative(),
    quotesCreated: z.number().int().nonnegative(),
    attributedOrders: z.number().int().nonnegative(),
    attributedRevenue: moneySchema,
    reactivatedCustomers: z.number().int().nonnegative(),
    reactivatedRevenue: moneySchema,
    irrelevantRecommendations: z.number().int().nonnegative(),
    falsePositiveRatePct: z.number().min(0).max(100).nullable(),
});
export const salesAssistantRuleQualitySchema = z.object({
    ruleId: idSchema,
    ruleName: z.string(),
    ruleType: salesRuleTypeSchema,
    generated: z.number().int().nonnegative(),
    processed: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    irrelevant: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    attributedOrders: z.number().int().nonnegative(),
    falsePositiveRatePct: z.number().min(0).max(100).nullable(),
});
export const salesAssistantTeamDistributionSchema = z.object({
    teamId: idSchema.nullable(),
    teamName: z.string(),
    openTasks: z.number().int().nonnegative(),
    overdueTasks: z.number().int().nonnegative(),
    completedTasks: z.number().int().nonnegative(),
});
export const salesAssistantInactiveTrendSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/u),
    customerCount: z.number().int().nonnegative(),
    basisCode: z.literal('GENERATED_INACTIVITY_RECOMMENDATIONS'),
});
export const salesAssistantRevenueAttributionSchema = z.object({
    taskId: idSchema,
    recommendationId: idSchema.nullable(),
    ruleId: idSchema.nullable(),
    customerId: idSchema,
    customerNumber: z.string(),
    customerName: z.string(),
    activityAt: isoDateTimeSchema,
    orderId: idSchema,
    orderNumber: z.string(),
    orderCreatedAt: isoDateTimeSchema,
    daysAfterActivity: z.number().int().nonnegative(),
    amount: moneySchema,
    currency: z.string().length(3),
    isReactivation: z.boolean(),
    basisCode: z.literal('ORDER_WITHIN_WINDOW_AFTER_DOCUMENTED_SALES_ACTIVITY'),
    causality: z.literal('TEMPORAL_ASSOCIATION_NOT_CAUSAL'),
});
export const salesAssistantAnalyticsResponseSchema = z.object({
    context: z.object({
        scope: salesAssistantAnalyticsScopeSchema,
        dateFrom: isoDateSchema,
        dateTo: isoDateSchema,
        timezone: z.string(),
        currency: z.string().length(3),
        attributionWindowDays: z.number().int().min(1).max(365),
        teamId: idSchema.nullable(),
        userId: idSchema.nullable(),
        employeeDetailsIncluded: z.boolean(),
        employeePerformanceAnalyticsEnabled: z.boolean(),
    }),
    availableTeams: z.array(z.object({ id: idSchema, name: z.string() })),
    availableEmployees: z.array(z.object({ id: idSchema, name: z.string() })),
    metrics: salesAssistantAnalyticsMetricsSchema,
    ruleQuality: z.array(salesAssistantRuleQualitySchema),
    teamDistribution: z.array(salesAssistantTeamDistributionSchema),
    inactiveCustomerTrend: z.array(salesAssistantInactiveTrendSchema),
    attributions: z.array(salesAssistantRevenueAttributionSchema),
    attributionMeta: paginationMetaSchema,
    trackingDisclosure: z.object({
        capturedFactCodes: z.array(z.enum([
            'RECOMMENDATION_ASSIGNMENT_AND_STATUS',
            'TASK_STATUS_AND_DUE_DATE',
            'DOCUMENTED_CONTACT_OUTCOME',
            'FOLLOW_UP_AND_QUOTE_CREATED',
            'ORDER_TIMESTAMP_AND_VALUE',
        ])),
        excludedFactCodes: z.array(z.enum(['KEYSTROKES', 'SCREEN_TIME', 'UNRELATED_EMPLOYEE_ACTIVITY', 'SENSITIVE_TRAITS'])),
        attributionCausality: z.literal('TEMPORAL_ASSOCIATION_NOT_CAUSAL'),
    }),
});
//# sourceMappingURL=sales-assistant.js.map